import React, { useEffect, useState } from "react";
import { Doughnut } from "react-chartjs-2";
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from "chart.js";
import classes from "./FlywheelStats.module.css";
import Card from "../Card/Card";
import ListTooltip from "../../ui/ListTooltip/ListTooltip";
import {
  FaArrowDownLong,
  FaArrowLeftLong,
  FaArrowRightLong,
  FaArrowUpLong,
} from "react-icons/fa6";
import RefreshIcon from "../../icon/RefreshIcon";
import { env } from "../../constants/env";

ChartJS.register(ArcElement, Tooltip, Legend);


const formatResult = (val) => {
  if (val === undefined || val === null) return "Inactive";
  return `${val > 0 ? "+" : ""}${val}%`;
};

const FlywheelStats = () => {
  const [isMobile, setIsMobile] = useState(false);
  const [loading, setLoading] = useState(false);
  const [dynamicMomentum, setDynamicMomentum] = useState("0%");
  const [strategies, setStrategies] = useState([]);

  const [stats, setStats] = useState({
    cohort: 0, // Cohort = Active + Failed (adjust if funded counts too)
    active: 0,
    failed: 0,
    funded: 0, 
    savedInChallengeFees: 0,
  }); 

  // Core fetch (no spinner logic here)
  const fetchStats = async () => {
    try {
      const res = await fetch(`${env.apiUrl}/api/flywheel-stats`);
      const data = await res.json();
      console.log("What is in dats", data);

      setStats({
        ...data,
        savedInChallengeFees:
          data.savedInChallengeFees?.toLocaleString("en-US"),
      });
    } catch (err) {
      console.error(err);
    }
  };

  const fetchData = async () => {
    try {
      const res = await fetch(`${env.apiUrl}/api/data-engine`);
      const data = await res.json();

      console.log("data-engine:", data);

      const mapped = [
        {
          name: "Hydra",
          result: formatResult(
            data.find((d) => d.strategy === "Hydra")?.YTD
          ),
          active: !!data.find((d) => d.strategy === "Hydra"),
        },
        {
          name: "Phoenix",
          result: formatResult(
            data.find((d) => d.strategy === "Phoenix")?.YTD
          ),
          active: !!data.find((d) => d.strategy === "Phoenix"),
        },
        {
          name: "Penny Picker",
          result: formatResult(
            data.find((d) => d.strategy === "Penny Picker")?.YTD
          ),
          active: !!data.find((d) => d.strategy === "Penny Picker"),
        },
      ];

      setStrategies(mapped);
    } catch (error) {
      console.error("Error fetching Data Engine:", error);
    }
  };

  // For RefreshIcon click (adds spinner)
  const handleRefresh = async () => {
    setLoading(true);
    await fetchStats();
    await fetchData();
    setLoading(false);
  };

  useEffect(() => {
    fetchStats();
    fetchData();
  }, []);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Stats config
  // const stats = {
  //   cohort: 140,
  //   active: 83,
  //   failed: 57,
  //   funded: 3,
  // };

  const chartData = {
    labels: ["Active", "Failed", "Funded"],
    datasets: [
      {
        data: [stats.active, stats.failed, stats.funded],
        backgroundColor: ["#1D6D00", "#970B0B", "#0163A5"],
        borderWidth: 0,
        cutout: "20%",
      },
    ],
  };

  const chartOptions = {
    plugins: {
      legend: { display: false },
      tooltip: {
        callbacks: {
          label: function (context) {
            const dataset = context.dataset;
            const total = dataset.data.reduce((acc, val) => acc + val, 0);
            const value = dataset.data[context.dataIndex];
            const percentage = ((value / total) * 100).toFixed(1);
            return `${context.label}: ${percentage}%`;
          },
        },
      },
    },
  };

  const topCards = [
    {
      value: stats.cohort,
      label: "Cohort Total",
      tooltip: "Total number of traders enrolled in the 2 step evaluation",
      tooltipCard: classes.tooltipCohort,
    },
    {
      value: stats.active,
      label: "Active",
      labelClass: classes.gradientText,
      tooltip: "Currently trading and in evaluation",
      tooltipCard: classes.tooltipActive,
    },
    {
      value: stats.failed,
      label: "Failed",
      labelClass: classes.red,
      tooltip: "Traders who did not pass the evaluation",
      tooltipCard: classes.tooltipFailed,
    },
    {
      value: stats.funded,
      label: "Funded Traders",
      tooltip: "Traders on the simulated live funded account",
      tooltipCard: classes.tooltipFunded,
    },
  ];

  const legendItems = [
    { label: "Active", value: stats.active, color: "#1D6D00" },
    { label: "Failed", value: stats.failed, color: "#970B0B" },
    { label: "Funded", value: stats.funded, color: "#0163A5" },
  ];

  // const strategies = [
  //   {
  //     name: "Dynamic Momentum",
  //     result: dynamicMomentum,
  //     active: true,
  //   },
  //   {
  //     name: "Early Bird",
  //     result: "Inactive",
  //     active: false,
  //   },
  //   {
  //     name: "Penny Picker",
  //     result: "Inactive",
  //     active: false,
  //   },
  // ];

  return (
    <div className={`container-fluid ${classes.wrapper}`}>
      <div className="d-flex align-items-center justify-content-between mb-4">
        <div className="d-flex align-items-center gap-2">
          <h2 className={`${classes.mainTitle}`}>Flywheel Stats</h2>{" "}
          <RefreshIcon onClick={handleRefresh} loading={loading} />
        </div>
        <h3 className={`m-0 ${classes.lastUpdated}`}>
          Updates Weekly
        </h3>
      </div>

      {/* Top Stats Row */}
      <div className="row mb-4">
        {topCards.map(
          ({ value, label, tooltip, labelClass, tooltipCard }, idx) => (
            <div className="col-md col-6 mt-md-0 mb-3" key={idx}>
              <Card>
                <h3>{value}</h3>
                <div className="d-flex gap-2 mt-3">
                  <p className={`${labelClass || ""} m-0`}>{label}</p>
                  <ListTooltip info={tooltip} tooltipCard={tooltipCard} />
                </div>
              </Card>
            </div>
          )
        )}
      </div>

      {/* Trader Distribution + Strategy Performance */}
      <div
        className={`${classes.equalRow} d-flex flex-row justify-content-center mb-md-4`}
      >
        {/* Distribution */}
        <div className="col-md-5 col-12">
          <h5 className={`mb-3 ${classes.subTitle}`}>Trader Distribution</h5>
          <Card className={`${classes.distributionCard} ${classes.equalCard}`}>
            <div className={classes.chartWrapper}>
              <Doughnut data={chartData} options={chartOptions} />
            </div>
            <div className="mt-3 mx-auto d-flex justify-content-center gap-3">
              {legendItems.map((item, idx) => (
                <div key={idx} className="d-flex align-items-center gap-2">
                  <div
                    className={classes.legend}
                    style={{ backgroundColor: item.color }}
                  />
                  {item.label}: {item.value}
                </div>
              ))}
            </div>
          </Card>
          <div className="mt-4 mx-auto">
            {isMobile ? (
              <FaArrowDownLong size={22} color="#CECECE" />
            ) : (
              <FaArrowUpLong size={30} color="#CECECE" />
            )}
          </div>
        </div>

        {/* Middle Arrow */}
        {!isMobile && (
          <div className="my-auto mx-4">
            <FaArrowRightLong size={30} color="#CECECE" />
          </div>
        )}

        {/* Strategy Performance */}
        <div className="col-md col-12">
          <div className="d-flex  gap-2 mb-3">
            <h5 className={`m-0 ${classes.subTitle}`}>
              Strategy Performance (ALL)
            </h5>
            <ListTooltip
              info="We turn trader activity into patterns our models use to generate trading signals"
              tooltipCard={classes.tooltipCard}
            />
          </div>
          <Card
            className={`${classes.strategyCard} ${classes.equalCard} d-flex flex-column justify-content-between`}
          >
            {strategies.map(({ name, result, active }, idx) => (
              <div
                key={idx}
                className={
                  active
                    ? classes.strategyBoxActive
                    : classes.strategyBoxInactive
                }
              >
                <span className={active ? "" : classes.inactiveNameText}>
                  {name}
                </span>
                <span
                  className={
                    active ? classes.dynamicNumber : classes.inactiveText
                  }
                >
                  {result}
                </span>
              </div>
            ))}
          </Card>
          <div className="mt-4 mx-auto">
            <FaArrowDownLong size={isMobile ? 22 : 30} color="#CECECE" />
          </div>
        </div>
      </div>

      {/* Bottom Row */}
      <div
        className={`${classes.equalRow} d-flex flex-row justify-content-center text-center`}
      >
        <div className="col-md col-12">
          <Card className={`${classes.comingSoonCard} ${classes.equalCard}`}>
            <div className="d-flex justify-content-center gap-2 mb-3">
              <h5 className="m-0">Copy Trading Launch</h5>
              <ListTooltip
                info="Traders will soon be able to copy our top-performing strategies."
                tooltipCard={classes.tooltipCard}
              />
            </div>
            <div className={classes.comingSoon}>Coming soon</div>
          </Card>
        </div>

        {isMobile ? (
          <div className="mb-4 d-flex justify-content-center w-100">
            <div>
              <FaArrowDownLong size={22} color="#CECECE" />
            </div>
          </div>
        ) : (
          <div className="my-auto mx-4">
            <FaArrowLeftLong size={30} color="#CECECE" />
          </div>
        )}

        <div className="col-md col-12">
          <Card className={`${classes.savedFessCard} ${classes.equalCard}`}>
            <h2>${stats.savedInChallengeFees}</h2>
            <div className="d-flex justify-content-center gap-2 mt-3">
              <p className="m-0">Saved In Challenge Fees</p>
              <ListTooltip
                info="Total challenge fees traders kept in their pockets thanks to PayLater model"
                tooltipCard={classes.tooltipCard}
              />
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default FlywheelStats;
